import { useEffect, useState } from "react";
import axios from "axios";
import { api } from "../utils/Api";

const range = (start, end) =>
	Array.from({ length: Math.max(end - start, 0) }, (_, i) => start + i);

export const filterOption = (input, option) =>
	(option?.label ?? "").toLowerCase().includes(input.toLowerCase());

// Disables days before minDate
export const getDisabledDate = (minDate) => (current) =>
	!!minDate && !!current && current < minDate.startOf("day");

// Disables hours/minutes before minDate on its same day.
// When strict, minDate's own minute is disabled too (value must be greater).
export const getDisabledTime = (minDate, strict) => (current) => {
	if (!minDate || !current || !current.isSame(minDate, "day")) {
		return {};
	}
	return {
		disabledHours: () => range(0, minDate.hour()),
		disabledMinutes: (hour) =>
			hour === minDate.hour()
				? range(0, minDate.minute() + (strict ? 1 : 0))
				: [],
	};
};

// Marks as disabled the options whose value is in unavailableValues
export const getAvailableOptions = (options, unavailableValues) => {
	const unavailable = new Set(unavailableValues);
	return options.map((option) => ({
		...option,
		disabled: unavailable.has(option.value),
	}));
};

// Loads the active drivers of the plant and the active trucks while the modal is open
export const useShiftGroupOptions = (open, plant, openNotification) => {
	const [loadingDrivers, setLoadingDrivers] = useState(false);
	const [loadingTrucks, setLoadingTrucks] = useState(false);
	const [driverOptions, setDriverOptions] = useState([]);
	const [truckOptions, setTruckOptions] = useState([]);

	useEffect(() => {
		if (open) {
			getTrucks();
		}
	}, [open]);

	useEffect(() => {
		if (open && plant) {
			getDrivers();
		}
	}, [open, plant]);

	const getDrivers = async () => {
		const url = api + `/shift-group/drivers/${plant}`;
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingDrivers(true);
			const { data } = await axios.get(url);
			if (data.success) {
				setDriverOptions(
					data.payload.map((item) => ({
						label: item.name,
						value: item._id,
					})),
				);
			} else {
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get drivers");
		} finally {
			setLoadingDrivers(false);
		}
	};

	const getTrucks = async () => {
		const url = api + "/truck/list/57Concrete/active";
		const token = localStorage.getItem("token");
		axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

		try {
			setLoadingTrucks(true);
			const { data } = await axios.get(url);
			setTruckOptions(
				data.map((item) => ({
					label: item.number,
					value: item._id,
				})),
			);
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", "Error while trying to get trucks");
		} finally {
			setLoadingTrucks(false);
		}
	};

	return { driverOptions, truckOptions, loadingDrivers, loadingTrucks };
};
